import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const FIR_SIZE = 10;
export const FIR_TARGET = 5;

export type FirCell = PlayerIndex | null;

export interface FiveInRowState extends BaseState {
	kind: "fiveinrow";
	board: FirCell[];
	lastMove: number | null;
	winningLine: number[] | null;
}

export interface FiveInRowMove {
	square: number;
}

const DIRECTIONS: readonly (readonly [number, number])[] = [
	[1, 0],
	[0, 1],
	[1, 1],
	[1, -1],
];

const at = (col: number, row: number): number => row * FIR_SIZE + col;

/** The run of five (or more) through this square, if there is one. */
export function findFirWin(board: FirCell[], square: number): number[] | null {
	const player = board[square];
	if (player === null) return null;
	const col = square % FIR_SIZE;
	const row = Math.floor(square / FIR_SIZE);

	for (const [dc, dr] of DIRECTIONS) {
		const line = [square];
		for (const sign of [1, -1]) {
			let c = col + dc * sign;
			let r = row + dr * sign;
			while (c >= 0 && c < FIR_SIZE && r >= 0 && r < FIR_SIZE && board[at(c, r)] === player) {
				line.push(at(c, r));
				c += dc * sign;
				r += dr * sign;
			}
		}
		if (line.length >= FIR_TARGET) return line;
	}
	return null;
}

export const fiveInRow: GameDefinition<FiveInRowState, FiveInRowMove> = {
	id: "fiveinrow",
	category: "puzzle",
	title: "Five in a Row",
	emoji: "⭕",
	blurb: "Like tic-tac-toe, but bigger — you need five in a row.",
	howTo: [
		"Take turns tapping any empty square on the big board.",
		"Get five of your marks in a line — across, down or diagonal.",
		"Watch out: blocking your friend matters as much as building your own line.",
	],

	create() {
		return {
			kind: "fiveinrow",
			board: Array<FirCell>(FIR_SIZE * FIR_SIZE).fill(null),
			lastMove: null,
			winningLine: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const square = (raw as { square?: unknown }).square;
		if (typeof square !== "number" || !Number.isInteger(square)) return null;
		if (square < 0 || square >= FIR_SIZE * FIR_SIZE) return null;
		return { square };
	},

	apply(state, player, move): MoveOutcome<FiveInRowState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.board[move.square] !== null) return reject("That square is taken.");

		const board = [...state.board];
		board[move.square] = player;

		const winningLine = findFirWin(board, move.square);
		if (winningLine) {
			const scores: [number, number] = [0, 0];
			scores[player] = 1;
			return {
				ok: true,
				state: {
					...state,
					board,
					scores,
					turn: null,
					result: { winner: player },
					lastMove: move.square,
					winningLine,
				},
			};
		}

		if (board.every((cell) => cell !== null)) {
			return {
				ok: true,
				state: { ...state, board, turn: null, result: { winner: null }, lastMove: move.square },
			};
		}

		return {
			ok: true,
			state: { ...state, board, turn: otherPlayer(player), lastMove: move.square },
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: FiveInRowMove[] = [];
		state.board.forEach((cell, square) => {
			if (cell === null) moves.push({ square });
		});
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
